import * as React from 'react';

// The feature checklist card (the `propose_options` block of propose_features) — PORTED from the old
// chat.html addOptionsCard: the groups of options as checkboxes, "Your own items" with an input and
// "Add", the submit button and its note. Submit composes the old plain-text selection and freezes the
// card into a record of the choice ("selection sent").

export interface OptionsConfig {
  title?: string;
  intro?: string;
  groups?: {
    label?: string;
    options?: { id?: string; label?: string; description?: string; checked?: boolean }[];
  }[];
  submit_label?: string;
}

interface Box {
  id: string;
  label: string;
  checked: boolean;
  custom?: boolean;
}

/** The old selection text: picked, the person's own items, and what was left unticked. */
export function selectionText(boxes: Box[]): string {
  const picked = boxes.filter((b) => !b.custom && b.checked).map((b) => b.label);
  const skipped = boxes.filter((b) => !b.custom && !b.checked).map((b) => b.label);
  const custom = boxes.filter((b) => b.custom && b.checked).map((b) => b.label);
  let text = `Selected features: ${picked.length ? picked.join(', ') : '(none)'}.`;
  if (custom.length) text += ` My own additional items (not from the proposed list): ${custom.join('; ')}.`;
  if (skipped.length) text += ` Not selected: ${skipped.join(', ')}.`;
  return text;
}

export function OptionsCard({
  config,
  onSubmit,
}: {
  config: OptionsConfig;
  onSubmit: (text: string, title: string) => void;
}) {
  const groups = (config.groups ?? []).filter((g) => g && Array.isArray(g.options) && g.options.length > 0);
  const [boxes, setBoxes] = React.useState<Box[]>(() =>
    groups.flatMap((g) =>
      (g.options ?? [])
        .filter((o) => o && o.label)
        .map((o) => ({ id: o.id || o.label!, label: o.label!, checked: !!o.checked })),
    ),
  );
  const [add, setAdd] = React.useState('');
  const [done, setDone] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const addCustom = (current: Box[]) => {
    const label = add.trim();
    if (!label) return current;
    setAdd('');
    inputRef.current?.focus();
    return [...current, { id: label, label, checked: true, custom: true }];
  };
  const toggle = (i: number) =>
    setBoxes((bs) => bs.map((b, j) => (j === i ? { ...b, checked: !b.checked } : b)));

  let k = 0;
  return (
    <div className={'viz-card' + (done ? ' opt-done' : '')} data-testid="chat-options-card">
      <div className="viz-head">
        <div className="viz-title">{config.title || 'Pick the features'}</div>
      </div>
      <div className="viz-content">
        {config.intro && <div className="opt-intro">{config.intro}</div>}
        {groups.map((g, gi) => (
          <React.Fragment key={gi}>
            {g.label && <div className="opt-group-label">{g.label}</div>}
            {(g.options ?? [])
              .filter((o) => o && o.label)
              .map((o) => {
                const i = k++;
                const b = boxes[i]!;
                return (
                  <label key={i} className="opt-row">
                    <input type="checkbox" checked={b.checked} disabled={done} onChange={() => toggle(i)} />
                    <div>
                      <div>{o.label}</div>
                      {o.description && <div className="opt-desc">{o.description}</div>}
                    </div>
                  </label>
                );
              })}
          </React.Fragment>
        ))}
        <div className="opt-group-label">Your own items</div>
        <div>
          {boxes.map((b, i) =>
            b.custom ? (
              <label key={i} className="opt-row">
                <input type="checkbox" checked={b.checked} disabled={done} onChange={() => toggle(i)} />
                <div>
                  <div>{b.label}</div>
                </div>
              </label>
            ) : null,
          )}
        </div>
        <div className="opt-add-row">
          <input
            ref={inputRef}
            type="text"
            placeholder="Add a feature that is not in the list…"
            value={add}
            disabled={done}
            data-testid="chat-options-add-input"
            onChange={(e) => setAdd(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                setBoxes((bs) => addCustom(bs));
              }
            }}
          />
          <button className="opt-add-btn" disabled={done} onClick={() => setBoxes((bs) => addCustom(bs))}>
            Add
          </button>
        </div>
        <div className="opt-footer">
          <button
            className="opt-submit"
            data-testid="chat-options-submit"
            disabled={done}
            onClick={() => {
              // Text still sitting in the add-input counts as an added item.
              const all = addCustom(boxes);
              setBoxes(all);
              setDone(true);
              onSubmit(selectionText(all), config.title || 'Pick the features');
            }}
          >
            {config.submit_label || 'Build with selected'}
          </button>
          <span className="opt-note">
            {done ? 'selection sent' : 'tick what you need, add your own — or just answer in the chat'}
          </span>
        </div>
      </div>
    </div>
  );
}
